import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const { FiChevronDown, FiChevronRight, FiCheck, FiSend, FiPlus, FiTrash2, FiMapPin, FiPhone, FiAlertTriangle } = FiIcons;

// --- COMPONENTES UI BASE ---

const CollapsibleSection = ({ title, children, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  return (
    <div className="mb-4 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden scroll-mt-20" id={title.replace(/\s+/g, '-').toLowerCase()}>
      <button 
        className="w-full text-left px-5 py-4 font-bold text-lg flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors text-slate-800"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span>{title}</span>
        <SafeIcon icon={isExpanded ? FiChevronDown : FiChevronRight} className="text-slate-400" />
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }} 
            animate={{ height: 'auto', opacity: 1 }} 
            exit={{ height: 0, opacity: 0 }} 
            className="overflow-hidden"
          >
            <div className="p-5 border-t border-slate-100 text-slate-700 leading-relaxed">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TaskCheckbox = ({ taskId, label, tasks, toggleTask, isHighlighted }) => {
  const isChecked = tasks[taskId] || false;
  return (
    <div className="flex items-start my-3 group cursor-pointer" onClick={() => toggleTask(taskId)}>
      <div className={`flex-shrink-0 w-5 h-5 mt-0.5 mr-3 rounded border transition-all flex items-center justify-center ${
        isChecked ? 'bg-red-600 border-red-600' : 'border-slate-300 group-hover:border-slate-400'
      } ${isHighlighted ? 'ring-2 ring-red-100' : ''}`}>
        {isChecked && <SafeIcon icon={FiCheck} className="text-white text-xs" />}
      </div>
      <span className={`text-sm select-none ${isChecked ? 'line-through text-slate-400' : 'text-slate-700'} ${isHighlighted ? 'font-bold text-red-600' : ''}`}>
        {label}
      </span>
    </div>
  );
};

const WarningBox = ({ children }) => (
  <div className="bg-red-50 border-l-4 border-red-500 p-4 my-4 text-sm text-red-800 font-medium rounded-r-lg flex gap-3">
    <SafeIcon icon={FiAlertTriangle} className="flex-shrink-0 mt-0.5" />
    <div>{children}</div>
  </div>
);

// --- SECCIONES DE CONTENIDO ---

const ObjetivoSection = () => (
  <div className="space-y-6">
    <p className="text-lg">
      Este documento sirve como guía para que el equipo de lavandería pueda ejecutar el reparto diario de vestuario en las taquillas de los usuarios, almacenes y el resto de puntos de intervención, siguiendo los protocolos establecidos para garantizar un servicio ordenado, higiénico y eficiente, incluso sin supervisión directa.
    </p>
    
    <div className="bg-slate-50 rounded-lg p-6 border border-slate-200 space-y-6">
      <div>
        <h3 className="font-bold text-slate-800 mb-2">Destinatarios:</h3>
        <p className="text-sm">Personal de lavandería encargado del reparto y recogida de ropa.</p>
      </div>

      <div>
        <h3 className="font-bold text-slate-800 mb-3">Responsable Iturri en planta:</h3>
        <a href="tel:649992732" className="inline-block bg-red-600 text-white text-sm font-bold px-5 py-2 rounded-full hover:bg-red-700 transition-colors">
          Alex | 649 992 732
        </a>
      </div>

      <div>
        <h3 className="font-bold text-slate-800 mb-3">Contacto incidencias:</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Mantenimiento 1', phone: '3392' },
            { label: 'Mantenimiento 2', phone: '3397' },
            { label: 'Garita Sur', phone: '3270' },
            { label: 'Garita Norte', phone: '3271' },
          ].map(c => (
            <a key={c.label} href={`tel:${c.phone}`} className="bg-red-600 text-white text-xs font-bold px-4 py-2 rounded-full hover:bg-red-700 transition-colors">
              {c.label} | {c.phone}
            </a>
          ))}
        </div>
      </div>
      
      <div className="pt-4 border-t border-slate-200">
        <div className="flex justify-center">
          <a href="https://goo.gl/maps/QZKGjvYL8YPU4QXHA" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-red-600 text-white text-sm font-bold px-6 py-2 rounded-full hover:bg-red-700 transition-colors">
            <SafeIcon icon={FiMapPin} /> VER MAPA DE ACCESO
          </a>
        </div>
      </div>
    </div>
  </div>
);

const ProtocoloSection = () => (
  <div className="space-y-8">
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 font-bold text-slate-800">2.1. Preparación</div>
      <div className="p-4">
        <ul className="list-disc pl-5 space-y-2 text-sm">
          <li>Los carros con el material deben prepararse en lavandería correctamente etiquetados con la zona correspondiente a la que pertenecen y cargados en el vehículo en el orden más adecuado para su descarga, con el fin de optimizar el reparto a sus respectivas áreas en la planta.</li>
        </ul>
      </div>
    </div>

    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 font-bold text-slate-800">2.2. Reparto en taquillas</div>
      <div className="p-4">
        <ul className="list-disc pl-5 space-y-2 text-sm">
          <li><strong>Usuarios nominativos:</strong> Abrir taquilla, dejar la ropa asignada. Comprobar que disponen de todas las prendas requeridas y abastecer con ropa genérica si es necesario.</li>
          <li><strong>Usuarios no nominativos/eventuales:</strong> Estas taquillas están identificadas con una flecha (↓). Cada taquilla debe tener en su interior la lista de prendas necesarias según el puesto. Si no fuera así, se debe informar al personal de almacén sobre localización y número de taquilla para poder corregirlo. Dejar las prendas correspondientes dentro de la taquilla.</li>
        </ul>
        <WarningBox>
          IMPORTANTE: cerrar correctamente todas las taquillas una vez completado el reparto en la zona.
        </WarningBox>
      </div>
    </div>

    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 font-bold text-slate-800">2.3. Reposición en almacén</div>
      <div className="p-4">
        <ul className="list-disc pl-5 space-y-2 text-sm">
          <li>Rellenar las estanterías de material de forma ordenada por tipo y tallas.</li>
          <li>Justo antes de finalizar el servicio, se debe revisar de nuevo el stock en los almacenes y dejar algún carro con material por si fuera necesario completar por el personal de almacén.</li>
        </ul>
      </div>
    </div>

    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 font-bold text-slate-800">2.4. Recogida de ropa sucia</div>
      <div className="p-4">
        <ul className="list-disc pl-5 space-y-2 text-sm">
          <li>Recoger los carros de ropa en cada zona, intentando agruparla siempre que sea posible para reducir el número de carros a trasportar.</li>
          <li>Recoger sacas de ropa sucia identificadas por zona. Verificar que las sacas estén cerradas y etiquetadas correctamente.</li>
          <li>Recoger todas las bolsas de guantes sucios de las 7 ubicaciones, a diario.</li>
        </ul>
      </div>
    </div>
  </div>
);

const ZonasSection = ({ tasks, toggleTask }) => {
  return (
    <div className="space-y-10">
      <div className="border-l-4 border-slate-300 pl-4">
        <h3 className="text-xl font-bold text-slate-800 mb-3">3.1. Zona Cuadras</h3>
        <p className="text-sm mb-4">
          Al entrar, bajo la escalera está situada la saca de ropa sucia. A la derecha, en el vestuario hay una pequeña estancia con la estantería donde se colocan los monos verdes del personal de esta zona. En época de frío, también se dejan aquí las parcas con cuello rojo.
        </p>
        <div className="bg-slate-50 p-4 rounded-lg">
          <TaskCheckbox taskId="cuadras-reposicion" label="Reposición estantería ropa no nominativa" tasks={tasks} toggleTask={toggleTask} />
          <TaskCheckbox taskId="cuadras-parcas" label="Entrega parcas de cuello rojo" tasks={tasks} toggleTask={toggleTask} />
          <TaskCheckbox taskId="cuadras-recogida" label="Recogida saca ropa sucia" tasks={tasks} toggleTask={toggleTask} />
        </div>
      </div>

      <div className="border-l-4 border-slate-300 pl-4">
        <h3 className="text-xl font-bold text-slate-800 mb-3">3.2. Nave 11 - Mantenimiento</h3>
        <p className="text-sm mb-4">
          En el pasillo de la entrada están las 28 taquillas de los usuarios de esta zona. No se requiere entrar en vestuario. Al final del pasillo, en la nave, se encuentra la saca de ropa sucia y un carro donde se deposita ropa no nominativa azul.
        </p>
        <div className="bg-slate-50 p-4 rounded-lg">
          <TaskCheckbox taskId="nave11-entrega" label="Entrega de vestuario en taquillas" tasks={tasks} toggleTask={toggleTask} />
          <TaskCheckbox taskId="nave11-reposicion" label="Reposición carro ropa no nominativa" tasks={tasks} toggleTask={toggleTask} />
          <TaskCheckbox taskId="nave11-recogida" label="Recogida saca ropa sucia" tasks={tasks} toggleTask={toggleTask} />
        </div>
      </div>

      <div className="border-l-4 border-slate-300 pl-4">
        <h3 className="text-xl font-bold text-slate-800 mb-3">3.3. Zona 9</h3>
        <p className="text-sm mb-3">
          Parar el vehículo junto a rampa de acceso. Al entrar, bajo la escalera, se deja un carro con ropa de mantenimiento (azul). En el pasillo principal hay taquillas de usuarios. Junto a la oficina de la izquierda hay una sala con más taquillas y una estantería de ropa no nominativa.
        </p>
        <WarningBox>
          IMPORTANTE: La saca junto a la estantería no se recoge salvo petición expresa. Se utiliza para prendas en mal estado.
        </WarningBox>
        <p className="text-sm mb-3">
          Al fondo de la sala hay una habitación cerrada para ropa de directivos y guantes de frío.
        </p>
        <WarningBox>
          IMPORTANTE: la ropa de directivos debe recogerse y lavarse por separado con especial cuidado.
        </WarningBox>
        <div className="bg-slate-50 p-4 rounded-lg">
          <TaskCheckbox taskId="zona9-entrega" label="Entrega vestuario en taquillas" tasks={tasks} toggleTask={toggleTask} />
          <TaskCheckbox taskId="zona9-reposicion" label="Reposición estantería ropa no nominativa" tasks={tasks} toggleTask={toggleTask} />
          <TaskCheckbox taskId="zona9-directivos" label="Entrega ropa directivos" tasks={tasks} toggleTask={toggleTask} />
          <TaskCheckbox taskId="zona9-guantes" label="Entrega guantes frío" tasks={tasks} toggleTask={toggleTask} />
          <TaskCheckbox taskId="zona9-carro" label="Reposición carro mantenimiento" tasks={tasks} toggleTask={toggleTask} />
          <TaskCheckbox taskId="zona9-recogida" label="Comprobar conducto de ropa sucia y recoger" tasks={tasks} toggleTask={toggleTask} />
        </div>
      </div>

      <div className="border-l-4 border-red-600 pl-4">
        <h3 className="text-xl font-bold text-red-600 mb-3">3.4. Zona Norte</h3>
        <p className="text-sm border-2 border-red-100 p-3 rounded-lg bg-red-50/30 mb-4">
          Es la zona de mayor tránsito y requiere especial atención. Para entrar en el área de despiece, es obligatorio el uso del burka desechable.
        </p>
        <div className="bg-slate-50 p-4 rounded-lg">
          <TaskCheckbox taskId="norte-carros-subir" label="Subir carros por montacargas a planta superior" tasks={tasks} toggleTask={toggleTask} />
          <TaskCheckbox taskId="norte-guantes-despiece" label="Recogida guantes sucios área despiece" tasks={tasks} toggleTask={toggleTask} isHighlighted />
          <TaskCheckbox taskId="norte-v1-v14" label="Reposición taquillas V1 a V14" tasks={tasks} toggleTask={toggleTask} />
          <TaskCheckbox taskId="norte-v32-v35" label="Reposición taquillas V32 a V35" tasks={tasks} toggleTask={toggleTask} />
          <TaskCheckbox taskId="norte-carros-almacen" label="Confirmar carros vacíos/limpios en vestuarios" tasks={tasks} toggleTask={toggleTask} />
        </div>
      </div>
    </div>
  );
};

const WhatsAppForm = ({ tasks }) => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [incidents, setIncidents] = useState([{ desc: '', type: 'Accidente', status: 'Pendiente' }]);

  const addIncident = () => setIncidents([...incidents, { desc: '', type: 'Accidente', status: 'Pendiente' }]);
  const updateIncident = (idx, field, val) => {
    const newInc = [...incidents];
    newInc[idx][field] = val;
    setIncidents(newInc);
  };
  const removeIncident = (idx) => {
    if (incidents.length > 1) setIncidents(incidents.filter((_, i) => i !== idx));
  };

  const sendWhatsapp = (e) => {
    e.preventDefault();
    const formattedDate = date.split('-').reverse().join('-');
    const validIncidents = incidents.filter(i => i.desc.trim());
    let text = `*Informe Servicio ECMEDINA*\n📅 ${formattedDate}\n\n`;
    if (validIncidents.length === 0) {
      text += "✅ *Sin incidencias registradas.*";
    } else {
      text += validIncidents.map((inc, i) => `⚠️ *Incidencia ${i+1}*\n${inc.desc}\n(${inc.type} - ${inc.status})`).join('\n\n');
    }
    window.open(`https://wa.me/34649992732?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <h3 className="font-bold text-lg mb-4 text-slate-800 flex items-center gap-2">
        <SafeIcon icon={FiSend} className="text-red-600" /> Informe Diario
      </h3>
      <form onSubmit={sendWhatsapp} className="space-y-4">
        <div>
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Fecha del Servicio</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full p-3 border rounded-xl mt-1 text-sm font-bold bg-slate-50" />
        </div>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
             <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Incidencias</label>
             <button type="button" onClick={addIncident} className="text-[10px] bg-red-50 text-red-600 px-3 py-1.5 rounded-full font-black flex items-center gap-1 uppercase">
               <SafeIcon icon={FiPlus} /> Añadir
             </button>
          </div>
          {incidents.map((inc, idx) => (
            <div key={idx} className="p-4 bg-slate-50 rounded-2xl border border-slate-200 relative">
              {incidents.length > 1 && (
                <button type="button" onClick={() => removeIncident(idx)} className="absolute top-3 right-3 text-red-400">
                  <SafeIcon icon={FiTrash2} />
                </button>
              )}
              <textarea placeholder="Describa el problema..." className="w-full p-2 text-sm border rounded-xl mb-3" rows="2" value={inc.desc} onChange={e => updateIncident(idx, 'desc', e.target.value)} />
              <div className="flex gap-2">
                <select className="text-xs p-2 rounded-lg border w-1/2 font-bold" value={inc.type} onChange={e => updateIncident(idx, 'type', e.target.value)}>
                  <option>Accidente</option>
                  <option>Instalaciones</option>
                  <option>Retraso</option>
                </select>
                <select className="text-xs p-2 rounded-lg border w-1/2 font-bold" value={inc.status} onChange={e => updateIncident(idx, 'status', e.target.value)}>
                  <option>Pendiente</option>
                  <option>Solucionada</option>
                </select>
              </div>
            </div>
          ))}
        </div>
        <button type="submit" className="w-full bg-green-600 text-white font-black py-4 rounded-2xl shadow-lg hover:bg-green-700 transition-all flex justify-center items-center gap-2 uppercase tracking-widest text-xs">
          <SafeIcon icon={FiSend} /> Enviar Informe WhatsApp
        </button>
      </form>
    </div>
  );
};

const ProtocolManual = () => {
  const [tasks, setTasks] = useState(() => {
    const saved = localStorage.getItem('minigiv_protocol_tasks');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('minigiv_protocol_tasks', JSON.stringify(tasks));
  }, [tasks]);

  const toggleTask = (id) => {
    setTasks(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="animate-fadeIn pb-24 px-4 sm:px-0 max-w-4xl mx-auto">
      <div className="text-center mb-8 pt-6">
        <h2 className="text-2xl font-black text-slate-800 tracking-tight">MANUAL OPERATIVO</h2>
        <p className="text-red-600 text-xs font-bold uppercase tracking-widest mt-1">Elaborados Cárnicos Medina</p>
      </div>

      <CollapsibleSection title="1. Introducción" defaultExpanded={true}>
        <ObjetivoSection />
      </CollapsibleSection>

      <CollapsibleSection title="2. Protocolo de Actuación">
        <ProtocoloSection />
      </CollapsibleSection>

      <CollapsibleSection title="3. Zonas y Tareas (Checklist)" defaultExpanded={true}>
        <ZonasSection tasks={tasks} toggleTask={toggleTask} />
      </CollapsibleSection>

      <CollapsibleSection title="4. Informe Final (WhatsApp)" defaultExpanded={true}>
        <WhatsAppForm tasks={tasks} />
      </CollapsibleSection>

      <div className="mt-12 p-6 bg-slate-100 rounded-3xl text-center">
        <p className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em]">
          Manual de Procedimiento Interno - ITURRI Group
        </p>
      </div>
    </div>
  );
};

export default ProtocolManual;